import * as React from "react";
import {Suspense} from "react";

import {StatefulKeypadContextProvider} from "@khanacademy/keypad-context";
import {AppShell} from "@khan/app-shell";
import {ErrorBoundary} from "@khan/component-error-boundary";
import ErrorLoading from "@khan/component-screens/error-loading";
import {ApolloRouter} from "@khan/data-gql-apollo";
import {GuideContextProvider, useGuideStateSyncEffect} from "@khan/guide-core";
import {PermissionsInfo} from "@khan/shared-core";
import {type ProductContext} from "@khan/shared-core/context";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import PageDisplayContainer from "#data-access/page-display-container.tsx";

import Router from "./router.tsx";

type Props = {
    // This should contain all of the routes we want a given app-shell
    // to render.
    children: React.ReactNode;

    // If set, this is the component to display when loading a page. AppShell
    // defines a Suspense boundary, and this is the Suspense fallback
    // (i.e. the component that's rendered when a promise is thrown).
    loadingComponent?: React.ReactNode;

    // The product context of the page.
    productContext: ProductContext;
};

// NOTE(kevinb): In order to use certain hooks they need to be rendered
// inside a component that is a child of certain "provider" components.
const LoggedInAppShellInner = ({children}: {children: React.ReactNode}) => {
    useGuideStateSyncEffect(); // requires <GuideContextProvider> as an ancestor

    return (
        <StatefulKeypadContextProvider>
            {children}
        </StatefulKeypadContextProvider>
    );
};

const LoggedOutAppShellInner = ({children}: {children: React.ReactNode}) => (
    <StatefulKeypadContextProvider>{children}</StatefulKeypadContextProvider>
);

export default function LegacyAppShell(props: Props): React.ReactElement {
    const loadingComponent = props.loadingComponent ?? <RouteLoadSpinner />;

    /**
     * PageDisplayContainer must wrap error boundary or we
     * don't get the statusCode and such when an error occurs.
     * If the PageDisplayContainer errors, then we have bigger
     * problems.
     */
    return (
        <PageDisplayContainer>
            <Router>
                <AppShell productContext={props.productContext}>
                    <ErrorBoundary
                        // We can't use the default error fallback here because
                        // it will try to render the page template (header, footer)
                        // which includes GraphQL operations. We want this
                        // render path to be as error-free as possible which means
                        // keeping it simple. Since GraphQL operations can fail
                        // and potentially cause another error, we currently avoid
                        // that. This may be revisited when we have time to consider
                        // a more robust approach.
                        errorFallback={() => <ErrorLoading />}
                    >
                        <ApolloRouter>
                            <GuideContextProvider>
                                {PermissionsInfo.isLoggedIn ? (
                                    <LoggedInAppShellInner>
                                        <Suspense fallback={loadingComponent}>
                                            {props.children}
                                        </Suspense>
                                    </LoggedInAppShellInner>
                                ) : (
                                    <LoggedOutAppShellInner>
                                        <Suspense fallback={loadingComponent}>
                                            {props.children}
                                        </Suspense>
                                    </LoggedOutAppShellInner>
                                )}
                            </GuideContextProvider>
                        </ApolloRouter>
                    </ErrorBoundary>
                </AppShell>
            </Router>
        </PageDisplayContainer>
    );
}
